// @ds/forms/useCrudModalForm — the boilerplate `useModalForm` block as a hook.
//
// Every CRUD form in the codebase repeats the same 30-line useModalForm
// configuration: resource, action-with-id-when-edit, queryOptions.enabled,
// redirect:false, warnWhenUnsavedChanges:false, onMutationSuccess →
// onSaved → close → onClose, successNotification with the right Polish
// message.
//
// This hook collapses that to a single call. Independent of SchemaForm —
// any hand-written form can adopt this and shed 30 LOC immediately.
//
// Pattern matches `useModalForm` from `@refinedev/antd` 1:1 — same return
// shape (`formProps`, `form`, `close`), augmented with `handleSubmit` /
// `handleClose` / `saving` for direct binding to `<CrudModalShell onSubmit
// onClose>`.

import { useCallback } from "react";
import { useModalForm } from "@refinedev/antd";
import { useTranslation } from "react-i18next";
import type { BaseRecord } from "@refinedev/core";
import type { FormInstance, FormProps } from "antd";

// ─── Inputs ──────────────────────────────────────────────────────────────

export interface UseCrudModalFormInput<T> {
  /** Refine resource name. */
  resource: string;
  /** "create" or "edit". Duplicate flows are "create" with seeded values. */
  action: "create" | "edit";
  /** Record id — required for edit mode. */
  id?: number | string;
  /** Modal open state — drives the edit-mode hydrate query. */
  open: boolean;
  /** Fires after the modal closes (× / Cancel / post-save). */
  onClose: () => void;
  /** Fires after a successful create / update with the resulting record. */
  onSaved?: (record: T) => void;
  /**
   * Override the success-toast message. Default: `common.messages.createSuccess`
   * (create) or `common.messages.updateSuccess` (edit). Pass `null` to suppress.
   */
  successMessage?: string | null;
  /**
   * Override the error-notification title shown when the create/update mutation
   * rejects. Default: `common.errors.create_failed` ("Nie udało się utworzyć")
   * or `common.errors.save_failed` ("Nie udało się zapisać"). The notification's
   * description is always the raw backend error message (`error.message`) —
   * operators need that to diagnose "duplicate name" / validation / permission
   * failures. Pass `null` to suppress the error notification entirely.
   */
  errorTitle?: string | null;
  /**
   * Refine `meta` — passed straight to dataProvider. Use for `select` strings
   * in edit mode when the hydrate query needs joined relations.
   */
  meta?: Record<string, unknown>;
}

// ─── Outputs ─────────────────────────────────────────────────────────────

export interface UseCrudModalFormOutput<T> {
  /** Spread into `<Form {...formProps}>`. */
  formProps: FormProps;
  /** Direct form instance — for setFieldsValue / getFieldValue / submit. The
   *  generic `T` reaches the returned `form` via its inferred value shape
   *  (AntD typing only carries it inside callbacks; consumers usually narrow
   *  at the call site if they need exhaustive value typing). */
  form: FormInstance<Partial<T>>;
  /** Programmatically close the modal-form state. Independent of `onClose`. */
  close: () => void;
  /** Wraps `form.submit()` — bind to `<CrudModalShell onSubmit>`. */
  handleSubmit: () => void;
  /** Closes the modal-form AND fires onClose — bind to `<CrudModalShell onClose>`. */
  handleClose: () => void;
  /** True while the create/update mutation is in flight. */
  saving: boolean;
}

// ─── Hook ────────────────────────────────────────────────────────────────

export function useCrudModalForm<T extends BaseRecord = BaseRecord>(
  opts: UseCrudModalFormInput<T>,
): UseCrudModalFormOutput<T> {
  const {
    resource,
    action,
    id,
    open,
    onClose,
    onSaved,
    successMessage,
    errorTitle,
    meta,
  } = opts;
  const { t } = useTranslation();
  const isEdit = action === "edit";

  const modal = useModalForm<T>({
    resource,
    action,
    ...(isEdit ? { id, queryOptions: { enabled: open && id != null } } : {}),
    redirect: false,
    warnWhenUnsavedChanges: false,
    ...(meta ? { meta } : {}),
    onMutationSuccess: ({ data }) => {
      onSaved?.(data);
      modal.close();
      onClose();
    },
    // Without this handler, useModalForm's internal mutation rejection
    // escapes as an unhandled promise rejection in tests. Refine's
    // errorNotification still fires the toast — this just absorbs the
    // rejection so the form stays mounted and the rejection doesn't leak
    // upward. Matches the legacy try/catch pattern hand-rolled forms used.
    onMutationError: () => {
      /* intentionally absorbed — see comment above */
    },
    successNotification:
      successMessage === null
        ? false
        : () => ({
            message:
              successMessage ??
              t(isEdit ? "common.messages.updateSuccess" : "common.messages.createSuccess"),
            type: "success",
          }),
    errorNotification:
      errorTitle === null
        ? false
        : (error) => ({
            message:
              errorTitle ??
              t(
                isEdit ? "common.errors.save_failed" : "common.errors.create_failed",
                isEdit ? "Nie udało się zapisać" : "Nie udało się utworzyć",
              ),
            description:
              (error as { message?: string } | undefined)?.message ??
              (typeof error === "string" ? error : ""),
            type: "error",
          }),
  });

  const form = (modal.formProps as { form: FormInstance<Partial<T>> }).form;

  const handleSubmit = useCallback(() => {
    form?.submit();
  }, [form]);

  const handleClose = useCallback(() => {
    modal.close();
    onClose();
  }, [modal, onClose]);

  const saving =
    (modal as unknown as { mutation?: { isPending?: boolean } }).mutation?.isPending ??
    (modal as unknown as { formLoading?: boolean }).formLoading ??
    false;

  return {
    formProps: modal.formProps as FormProps,
    form,
    close: modal.close,
    handleSubmit,
    handleClose,
    saving,
  };
}
